import type { SignupStep } from './signupAnalytics';

/**
 * A post-signup ROUTE the orchestrator detours a signup through on its way to
 * `/pricing-plans` — a route, not an overlay, so pricing can Back into it.
 *
 * Two kinds, distinguished by {@link SignupWalkthrough.intent}: the campaign
 * explainers, selected by their `utm_regintent`, and the content taste, which
 * is selected from what the signup did rather than from a link parameter.
 * Either kind differs in these three values and nothing else.
 */
export interface SignupWalkthrough {
  path: string;
  /**
   * The one `utm_regintent` its campaign links carry, or `null` for a
   * walkthrough that is not campaign-keyed at all.
   *
   * `null` is not "any intent" — it is "never selected BY intent". The
   * content taste is chosen from what the signup actually did (an ECHO signup
   * that connected socials, so an analysis is genuinely running), which no
   * campaign parameter can tell you. It still needs a row here, because a row
   * is what the three plan gates read through `isPlanSelectionSurface` — the
   * whole reason this list exists in one place.
   */
  intent: string | null;
  /** Funnel step the orchestrator reports when it picks this route. */
  step: SignupStep;
  /**
   * What to CALL this surface in a control that returns to it — pricing's Back
   * button, which names its destination in an `aria-label`.
   *
   * On the row because the destination is no longer always the explainer: the
   * Growth Rx funnel runs `/growth-rx-walkthrough` → `/taste` → `/pricing-plans`
   * for an IG signup, so Back from the paywall lands on the taste and a
   * hardcoded "Back to walkthrough" announced the wrong screen. Lower case: it
   * is read inside a sentence ("Back to …"), never on its own.
   */
  label: string;
}

export const GROWTH_RX_WALKTHROUGH: SignupWalkthrough = {
  path: '/growth-rx-walkthrough',
  intent: 'growthrx_agent',
  step: 'growthrx-explainer',
  label: 'walkthrough',
};

export const SOCIAL_ENGAGEMENT_WALKTHROUGH: SignupWalkthrough = {
  path: '/social-engagement-walkthrough',
  intent: 'engagement_agent',
  step: 'engagement-agent-explainer',
  label: 'walkthrough',
};

/**
 * The pre-paywall Growth Rx content taste — the creator's own posts, the
 * learning orb, and three days of content written in their voice, on the way to
 * pricing.
 *
 * `intent: null` because it is not selected by `utm_regintent`, which is the
 * only thing that field means. It IS campaign-gated — on
 * `utm_onboarding_path=growthrx_agent` (`isGrowthRxOnboardingPath`), which BEATS
 * a `utm_regintent` explainer when a link carries both — ANDed with an ECHO
 * signup that connected socials, the condition under which the voice profile it
 * waits on will actually be written.
 *
 * ⚠️ IT IS NO LONGER A REDIRECT TARGET OF SIGNUP (2026-09-17). A Growth Rx
 * arrival meets `/growth-rx-walkthrough` first — while `popstore.taste_enabled`
 * is on at all, which since 2026-09-21 governs BOTH screens together
 * (`growthRxDetour`); the pair of conditions above then decides whether the
 * taste FOLLOWS the explainer — `tasteFollowsWalkthrough`,
 * asked by `useFinalizeCreatorOnboarding` and travelling to the explainer as
 * `?onward=taste` for its CTA to read. Everyone else goes from the explainer
 * straight to `/pricing-plans`. Until then the two screens were ALTERNATIVES
 * and this row was a destination the orchestrator picked. See `TastePage`.
 */
export const TASTE: SignupWalkthrough = {
  path: '/taste',
  intent: null,
  step: 'taste',
  label: 'your content preview',
};

export const SIGNUP_WALKTHROUGHS: readonly SignupWalkthrough[] = [
  GROWTH_RX_WALKTHROUGH,
  SOCIAL_ENGAGEMENT_WALKTHROUGH,
  TASTE,
];

const WALKTHROUGH_RETURN_PARAM = 'from';

/**
 * `?onward=<walkthrough>` — the screen THIS walkthrough hands off to, instead
 * of `/pricing-plans`.
 *
 * FUNNEL STATE, not attribution, exactly like `?step=` — so it is registered in
 * `kInternalFunnelParamKeys` and never reaches `onboardingMarketingCapture` or
 * HubSpot. It rides on the URL because the answer is known where the redirect is
 * BUILT and not where it lands: `useFinalizeCreatorOnboarding` holds both of the
 * taste's conditions in hand (the live `popstore.taste_enabled` and the identity
 * the account was just created with), while `/growth-rx-walkthrough` is a client
 * route that can cheaply read neither — `signedUpWithSocialLogin` is
 * `server-only` and reads Firestore.
 *
 * ABSENT MEANS PRICING, which is what makes every other arrival at this route
 * correct by default: a direct visit, a refresh, a shared link. Only the signup
 * detour ever mints one, and only when the taste genuinely follows.
 *
 * ⚠️ NOT `next`, which it was for an afternoon. `next` is this app's
 * login-redirect parameter — `/login?next=<path>` is written by every auth gate
 * (`WithCreatorDashboard`, `requireSession`, the OAuth popup callback) and READ
 * as a path to send the creator to. A funnel marker with the same name whose
 * value is not a path is a collision waiting for the one place that copies a
 * query string onto a login URL. `onward` is not read by anything else.
 *
 * It travels ONE hop. `walkthroughUrlWithOnward` stamps it onto the explainer's
 * URL; `urlMarkedFrom` — every hand-off OUT of a walkthrough — strips it, because
 * a hand-off is the stamp being consumed. Left on, it rode onto the taste and
 * then onto pricing, where nothing reads it but where it would have been one
 * more param to leak into a checkout or a share.
 */
export const WALKTHROUGH_ONWARD_PARAM = 'onward';

/** Derived from the route so the two spellings cannot drift. */
function markerFor(walkthrough: SignupWalkthrough): string {
  return walkthrough.path.replace(/^\//, '');
}

export function isSignupWalkthroughPath(pathname: string): boolean {
  return SIGNUP_WALKTHROUGHS.some(walkthrough => walkthrough.path === pathname);
}

/**
 * The walkthrough one of these values asks for. Several candidates because the
 * intent arrives either as the context intent or as the captured
 * `utm_regintent` — the connect-socials host has only the latter.
 */
export function walkthroughForSignupIntent(
  ...intents: (string | undefined)[]
): SignupWalkthrough | undefined {
  const normalized = intents
    .map(intent => (intent ?? '').toString().trim().toLowerCase())
    .filter(Boolean);
  return SIGNUP_WALKTHROUGHS.find(
    // An intent-less walkthrough is never selected here — see
    // `SignupWalkthrough.intent`. Guarded rather than filtered out so a null
    // can never coincide with a missing `utm_regintent` and match everything.
    walkthrough =>
      walkthrough.intent !== null && normalized.includes(walkthrough.intent),
  );
}

/**
 * `path`, carrying the current query forward (dropping the campaign params
 * would end the attribution chain a hop early) plus the marker naming the
 * surface being left.
 */
function urlMarkedFrom(path: string, origin: SignupWalkthrough): string {
  const params = new URLSearchParams(
    typeof window === 'undefined' ? undefined : window.location.search,
  );
  params.set(WALKTHROUGH_RETURN_PARAM, markerFor(origin));
  // The onward stamp is CONSUMED by the hand-off that reads it — see
  // `WALKTHROUGH_ONWARD_PARAM`. Carrying it past the walkthrough put
  // `onward=taste` on the taste's own URL and then on pricing's.
  params.delete(WALKTHROUGH_ONWARD_PARAM);
  return `${path}?${params.toString()}`;
}

/**
 * The pricing URL a walkthrough's CTA soft-navs to — the marker is what
 * unlocks pricing's return button.
 */
export function pricingPlansFromWalkthroughUrl(
  walkthrough: SignupWalkthrough,
): string {
  return urlMarkedFrom('/pricing-plans', walkthrough);
}

/**
 * One walkthrough's route, entered FROM another — today only the taste's "See
 * how Growth Rx works", which opens the explainer.
 *
 * The marker is the whole point: the explainer's own exits read it and go back
 * to `origin` instead of on to pricing, so a creator who opened it from the
 * taste is returned to the screen they left rather than pushed a step forward
 * in a funnel they were reading sideways.
 */
export function walkthroughUrlFrom(
  target: SignupWalkthrough,
  origin: SignupWalkthrough,
): string {
  return urlMarkedFrom(target.path, origin);
}

/**
 * The surface this arrival came out of. The marker is the whole gate: only a
 * surface that hands off deliberately mints one.
 *
 * Read on TWO routes, which is why it is not named for either: pricing asks it
 * to decide whether to grow a "Back to walkthrough" button, and a walkthrough
 * asks it to decide whether its exits go back or on to pricing.
 */
export function walkthroughFromArrival(
  searchParams: { get(name: string): string | null } | null,
): SignupWalkthrough | undefined {
  const marker = searchParams?.get(WALKTHROUGH_RETURN_PARAM);
  if (!marker) return undefined;
  return SIGNUP_WALKTHROUGHS.find(
    walkthrough => markerFor(walkthrough) === marker,
  );
}

/**
 * {@link walkthroughFromArrival} against the LIVE url, for a client handler
 * that runs on a press rather than during render — the same reason the builders
 * above read `window.location.search` instead of taking params.
 */
export function walkthroughFromCurrentUrl(): SignupWalkthrough | undefined {
  if (typeof window === 'undefined') return undefined;
  return walkthroughFromArrival(new URLSearchParams(window.location.search));
}

/**
 * The mirror of {@link pricingPlansFromWalkthroughUrl}: same params minus the
 * marker, which describes a pricing arrival and is re-minted on the next press.
 */
export function walkthroughReturnUrl(walkthrough: SignupWalkthrough): string {
  const params = new URLSearchParams(
    typeof window === 'undefined' ? undefined : window.location.search,
  );
  params.delete(WALKTHROUGH_RETURN_PARAM);
  /* `onward=` DESCRIBES A HAND-OFF ALREADY MADE, so going back must not carry it.
     Left on, a creator who reached the taste through `?onward=taste` and pressed
     Back on pricing returned to the explainer with the stamp intact — and its
     CTA, reading the stamp, sent them forward into the taste a second time
     instead of to the paywall they had just come from. Stripped here for the
     same reason the return marker is: both are statements about an arrival, not
     about the screen being returned to. */
  params.delete(WALKTHROUGH_ONWARD_PARAM);
  const query = params.toString();
  return query ? `${walkthrough.path}?${query}` : walkthrough.path;
}

/**
 * The URL that sends a creator to `walkthrough` AND tells it to hand off to
 * `onward` rather than to `/pricing-plans`.
 *
 * Built by the signup router, which is the only place that knows both of the
 * taste's conditions — see {@link WALKTHROUGH_ONWARD_PARAM}. `onward` is optional
 * so the caller can pass the answer straight through without branching: an
 * absent one yields the plain route, which already means "on to pricing".
 *
 * NOT `urlMarkedFrom`: that stamps `from=`, which is a statement about where
 * the creator CAME from and is what grows pricing's Back button. This one says
 * where they are GOING, and the two markers coexist on one URL — a taste
 * arrival carries `from=growth-rx-walkthrough` to make Back work while the
 * explainer that sent it there carried `onward=taste`.
 */
export function walkthroughUrlWithOnward(
  walkthrough: SignupWalkthrough,
  onward?: SignupWalkthrough,
): string {
  // The live query carried forward, for the same reason `urlMarkedFrom` carries
  // it: this is a hop INSIDE the funnel, and dropping the campaign params would
  // end the attribution chain one screen early.
  const params = new URLSearchParams(
    typeof window === 'undefined' ? undefined : window.location.search,
  );
  // A marker describes the arrival being LEFT, so it never rides along.
  params.delete(WALKTHROUGH_RETURN_PARAM);
  if (onward) params.set(WALKTHROUGH_ONWARD_PARAM, markerFor(onward));
  else params.delete(WALKTHROUGH_ONWARD_PARAM);
  const query = params.toString();
  return query ? `${walkthrough.path}?${query}` : walkthrough.path;
}

/**
 * The walkthrough this arrival was told to hand off to, or `undefined` for the
 * ordinary "on to `/pricing-plans`".
 *
 * Read on a press rather than during render — hence the live URL, the same
 * reason {@link walkthroughFromCurrentUrl} exists.
 */
export function walkthroughOnwardFromCurrentUrl():
  | SignupWalkthrough
  | undefined {
  if (typeof window === 'undefined') return undefined;
  const marker = new URLSearchParams(window.location.search).get(
    WALKTHROUGH_ONWARD_PARAM,
  );
  if (!marker) return undefined;
  return SIGNUP_WALKTHROUGHS.find(
    walkthrough => markerFor(walkthrough) === marker,
  );
}
